import React, { useRef } from 'react'
import styled from 'styled-components'

import ShareAlert from 'features/alerts/components/share-alert/ShareAlert'
import ViewAlert from 'features/alerts/components/view-alert/ViewAlert'
import CreateAlert from 'features/alerts/features/create-alert/Container'
import { AlertResponse, AlertService } from 'features/alerts/service'
import type { FilterOption } from 'features/filters/types'
import { usePanelSentryTransaction } from 'trackers/sentry'
import { Filters, RouteType } from 'types/project'

type Props = {
  alertData?: AlertResponse
  isShare?: boolean
  isView?: boolean
  projectId?: string
  route?: RouteType
  isAlertDrawerOpen?: boolean
  setIsAlertDrawerOpen?: (isOpen: boolean) => void
  alertRequest?: Filters | undefined
  refetch?: () => Promise<unknown>
  isPublicViewer?: boolean
  onAlertUpserted?: (alert: AlertResponse) => void
  filterOptions?: FilterOption[]
  alertService: AlertService
}
export const AlertDrawer: React.FC<Props> = ({
  alertData,
  isShare,
  isView,
  projectId,
  route,
  isAlertDrawerOpen,
  setIsAlertDrawerOpen,
  alertRequest,
  refetch,
  isPublicViewer,
  onAlertUpserted,
  filterOptions,
  alertService,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  usePanelSentryTransaction(isAlertDrawerOpen ? 'alert' : null)

  return (
    <>
      <OverlayContainer
        $isOpen={!!isAlertDrawerOpen}
        onClick={() => {
          setIsAlertDrawerOpen?.(false)
        }}
      ></OverlayContainer>
      <Drawer ref={ref} $isOpen={!!isAlertDrawerOpen}>
        {isAlertDrawerOpen && (
          <Wrapper>
            {isShare && alertData ? (
              <ShareAlert
                alertData={alertData}
                setIsOpen={setIsAlertDrawerOpen!}
                refetch={refetch!}
                projectId={projectId ?? ''}
                alertService={alertService}
              />
            ) : (
              <>
                {isView ? (
                  <ViewAlert
                    key={alertData?.alert?.id}
                    alertData={alertData}
                    setIsOpen={setIsAlertDrawerOpen!}
                    refetch={refetch!}
                    filterOptions={filterOptions ?? []}
                  />
                ) : (
                  <CreateAlert
                    key={alertData?.alert?.id ?? JSON.stringify(alertRequest)}
                    alertRecord={alertData}
                    setIsOpen={setIsAlertDrawerOpen!}
                    alertRequest={alertRequest}
                    projectId={projectId ?? ''}
                    isPublicViewer={isPublicViewer ?? false}
                    route={route!}
                    isAlertDrawerOpen={isAlertDrawerOpen ?? false}
                    onAlertUpserted={onAlertUpserted!}
                  />
                )}
              </>
            )}
          </Wrapper>
        )}
      </Drawer>
    </>
  )
}

const OverlayContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${(props) => (props.$isOpen ? 200 : -1)};
  transition: background-color 0.2s ease-in-out;
  background: ${(props) => (props.$isOpen ? 'rgba(0, 0, 0, 0.2)' : 'none')};
`
const Drawer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  z-index: 201;
  height: 100vh;
  width: 50%;
  max-width: 750px;
  background: white;
  right: 0;
  top: 0;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
  box-shadow: ${(props) =>
    props.$isOpen
      ? '0px 0px 100px -20px rgba(50, 50, 93, 0.25), -10px 0px 60px -30px rgba(0, 0, 0, 0.3)'
      : 'none'};
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0%)' : 'translateX(100%)'};
  transition: 0.25s ease-in-out;
  overflow: hidden;
`

const Wrapper = styled.div`
  position: relative;
  background: white;
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
  height: 100%;
`
